'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  Field,
  LabelDefault,
  OBJECT_EDIT_FIELD_GROUP_TWO_COLUMN_MIN_WIDTH_PX,
  ObjectEditField,
  ObjectEditFieldGroup,
  ObjectEditSection,
  RichMarkdownEditor,
  StructuredPocEditor,
  type EditableFields,
} from '@terra/ui-design-system';
import type { PocBlock } from '@terra/shared-schemas-orchestrator';
import { ObjectContextMarkableField } from '../object-context/ObjectContextMarkableField';
import { usePocBlocksSuggestionLabels } from '../../hooks/usePocBlocksSuggestionLabels';
import type { FindingEditableField } from '../../store/findingEditStore';
import { formatPocBlocksSuggestionValue } from '../../utils/findingSuggestionFieldUtils';
import type {
  FindingEditMarkableFieldProps,
  FindingEditSendFieldContext,
} from './findingEditForm.types';

const POC_BLOCKS_EDITOR_ACTION_PORTAL_ID = 'poc-blocks-editor-action-portal';

type FindingEditTechnicalDetailsSectionProps = {
  findingId: string;
  editableFields: EditableFields;
  hasUnsavedChanges: boolean;
  sendSelectionToChatLabel: string;
  handleFieldChange: <T extends keyof EditableFields>(
    field: T,
    value: EditableFields[T],
  ) => void;
  handlePocBlocksChange: (blocks: PocBlock[]) => void;
  handleSendFieldContextToChat: (context: FindingEditSendFieldContext) => void;
  getMarkableFieldProps: (
    field: FindingEditableField,
  ) => FindingEditMarkableFieldProps;
};

export function FindingEditTechnicalDetailsSection({
  findingId,
  editableFields,
  hasUnsavedChanges,
  sendSelectionToChatLabel,
  handleFieldChange,
  handlePocBlocksChange,
  handleSendFieldContextToChat,
  getMarkableFieldProps,
}: FindingEditTechnicalDetailsSectionProps) {
  const t = useTranslations('designSystem.findings.editDrawer');
  const pocSuggestionLabels = usePocBlocksSuggestionLabels();
  const pocBlocksMarkableProps = getMarkableFieldProps('pocBlocks');
  const hasPendingPocBlocks =
    pocBlocksMarkableProps.pendingEdit != null &&
    pocBlocksMarkableProps.onAcceptPendingEdit != null &&
    pocBlocksMarkableProps.onDiscardPendingEdit != null;
  const hasStructuredPocBlocks = (editableFields.pocBlocks?.length ?? 0) > 0;
  const pocLabel = t('sections.technicalDetails.fields.poc.label');
  const formatPocSuggestionValue = useCallback(
    (value: unknown) =>
      formatPocBlocksSuggestionValue({
        value,
        labels: pocSuggestionLabels,
      }),
    [pocSuggestionLabels],
  );
  const pocBlocksFullText = formatPocSuggestionValue(editableFields.pocBlocks);

  return (
    <ObjectEditSection title={t('sections.technicalDetails.title')}>
      <ObjectEditFieldGroup>
        <ObjectEditField span="full">
          <Field.Root
            data-field-anchor="pocBlocks"
            data-pending-review-field={
              hasPendingPocBlocks ? 'pocBlocks' : undefined
            }
          >
            <HStack justifyContent="space-between" alignItems="center" gap={4}>
              <Field.Label asChild>
                <LabelDefault flex={1}>{pocLabel}</LabelDefault>
              </Field.Label>
              <Box id={POC_BLOCKS_EDITOR_ACTION_PORTAL_ID} flexShrink={0} />
            </HStack>
            {hasStructuredPocBlocks || hasPendingPocBlocks ? (
              <ObjectContextMarkableField
                field="pocBlocks"
                fieldLabel={pocLabel}
                fullText={pocBlocksFullText}
                sendToChatLabel={sendSelectionToChatLabel}
                {...pocBlocksMarkableProps}
                formatPendingValue={formatPocSuggestionValue}
                onSendSelection={handleSendFieldContextToChat}
              >
                <StructuredPocEditor
                  actionPortalId={POC_BLOCKS_EDITOR_ACTION_PORTAL_ID}
                  blocks={editableFields.pocBlocks ?? []}
                  findingId={findingId}
                  isDraftDirty={hasUnsavedChanges}
                  onChange={handlePocBlocksChange}
                />
              </ObjectContextMarkableField>
            ) : (
              <ObjectContextMarkableField
                field="poc"
                fieldLabel={pocLabel}
                fullText={editableFields.poc ?? ''}
                sendToChatLabel={sendSelectionToChatLabel}
                {...getMarkableFieldProps('poc')}
                onSendSelection={handleSendFieldContextToChat}
              >
                <RichMarkdownEditor
                  value={editableFields.poc ?? ''}
                  onChange={(value) => handleFieldChange('poc', value)}
                  placeholder={t(
                    'sections.technicalDetails.fields.poc.placeholder',
                  )}
                  height={400}
                  preview="live"
                  stackBelowWidthPx={
                    OBJECT_EDIT_FIELD_GROUP_TWO_COLUMN_MIN_WIDTH_PX
                  }
                />
              </ObjectContextMarkableField>
            )}
          </Field.Root>
        </ObjectEditField>

        <ObjectEditField span="full">
          <Field.Root>
            <Field.Label asChild>
              <LabelDefault>
                {t('sections.technicalDetails.fields.businessImpact.label')}
              </LabelDefault>
            </Field.Label>
            <ObjectContextMarkableField
              field="businessImpact"
              fieldLabel={t(
                'sections.technicalDetails.fields.businessImpact.label',
              )}
              fullText={editableFields.businessImpact ?? ''}
              sendToChatLabel={sendSelectionToChatLabel}
              {...getMarkableFieldProps('businessImpact')}
              onSendSelection={handleSendFieldContextToChat}
            >
              <RichMarkdownEditor
                value={editableFields.businessImpact ?? ''}
                onChange={(value) => handleFieldChange('businessImpact', value)}
                placeholder={t(
                  'sections.technicalDetails.fields.businessImpact.placeholder',
                )}
                height={320}
                preview="live"
                stackBelowWidthPx={
                  OBJECT_EDIT_FIELD_GROUP_TWO_COLUMN_MIN_WIDTH_PX
                }
              />
            </ObjectContextMarkableField>
          </Field.Root>
        </ObjectEditField>

        <ObjectEditField span="full">
          <Field.Root>
            <Field.Label asChild>
              <LabelDefault>
                {t('sections.technicalDetails.fields.mitigationPlan.label')}
              </LabelDefault>
            </Field.Label>
            <ObjectContextMarkableField
              field="mitigationPlan"
              fieldLabel={t(
                'sections.technicalDetails.fields.mitigationPlan.label',
              )}
              fullText={editableFields.mitigationPlan ?? ''}
              sendToChatLabel={sendSelectionToChatLabel}
              {...getMarkableFieldProps('mitigationPlan')}
              onSendSelection={handleSendFieldContextToChat}
            >
              <RichMarkdownEditor
                value={editableFields.mitigationPlan ?? ''}
                onChange={(value) => handleFieldChange('mitigationPlan', value)}
                placeholder={t(
                  'sections.technicalDetails.fields.mitigationPlan.placeholder',
                )}
                height={320}
                preview="live"
                stackBelowWidthPx={
                  OBJECT_EDIT_FIELD_GROUP_TWO_COLUMN_MIN_WIDTH_PX
                }
              />
            </ObjectContextMarkableField>
          </Field.Root>
        </ObjectEditField>
      </ObjectEditFieldGroup>
    </ObjectEditSection>
  );
}
